import React, { useEffect, useState } from 'react';
import slideshow4 from '../../assets/images/slideshow4.jpg';
import slideshow5 from '../../assets/images/slideshow5.jpg';
import whitePurple from '../../assets/images/white_purple.jpg';
import whiteRed from '../../assets/images/white_red.jpg';
import redpurpleGreen from '../../assets/images/redpurple_green.jpg';
import purpleWhite from '../../assets/images/purple_white.jpg';
import blueBlack from '../../assets/images/blue_black.jpg';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Tooltip } from '@mui/material';

const Slideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slideshowImages = [
    { 
      src: slideshow5, 
      alt: 'Braille Skills Puzzle with green and purple interlocking pieces featuring tactile shapes including circles, triangles, squares, pentagons, and various Braille patterns for hands-on learning',
      tooltip: 'Braille Skills Puzzle with green and purple interlocking pieces featuring tactile shapes including circles, triangles, squares, pentagons, and various Braille patterns for hands-on learning'
    },
    { 
      src: slideshow4, 
      alt: 'Braille Skills Puzzle showing blue and black interlocking pieces with tactile shapes including crosses, circles, triangles, and Braille dot patterns designed for tactile learning',
      tooltip: 'Braille Skills Puzzle showing blue and black interlocking pieces with tactile shapes including crosses, circles, triangles, and Braille dot patterns designed for tactile learning'
    },
    { 
      src: whitePurple, 
      alt: 'Braille Skills Puzzle with white and purple pieces featuring raised tactile patterns and Braille numerals for enhanced learning',
      tooltip: 'Braille Skills Puzzle with white and purple pieces featuring raised tactile patterns and Braille numerals for enhanced learning'
    },
    { 
      src: whiteRed, 
      alt: 'Braille Skills Puzzle displaying white and red interlocking pieces with geometric shapes and Braille dots',
      tooltip: 'Braille Skills Puzzle displaying white and red interlocking pieces with geometric shapes and Braille dots'
    },
    { 
      src: redpurpleGreen, 
      alt: 'Braille Skills Puzzle featuring red, purple, and green pieces with tactile symbols and Braille patterns',
      tooltip: 'Braille Skills Puzzle featuring red, purple, and green pieces with tactile symbols and Braille patterns'
    },
    { 
      src: purpleWhite, 
      alt: 'Braille Skills Puzzle with purple and white pieces showing distinct tactile shapes and Braille numerals',
      tooltip: 'Braille Skills Puzzle with purple and white pieces showing distinct tactile shapes and Braille numerals'
    },
    { 
      src: blueBlack, 
      alt: 'Braille Skills Puzzle with blue and black pieces featuring raised geometric patterns and Braille dots for tactile exploration',
      tooltip: 'Braille Skills Puzzle with blue and black pieces featuring raised geometric patterns and Braille dots for tactile exploration'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slideshowImages.length) % slideshowImages.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-advance slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, [slideshowImages.length]);

  return (
    <section className="py-16 px-6" style={{ backgroundColor: 'var(--background-color)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--home-text-primary)' }}>
            See Our <span style={{ color: 'var(--home-text-primary)' }}>Puzzles</span> in Action
          </h2>
          <p className="text-lg max-w-3xl mx-auto leading-relaxed" style={{ color: 'var(--home-text-secondary)' }}>
            Explore our innovative Braille learning puzzles designed to enhance tactile literacy and make learning engaging.
          </p>
        </motion.div>

        {/* Slideshow */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="relative w-full max-w-6xl mx-auto"
        >
          {/* Slideshow container with fixed aspect ratio */}
          <div 
            className="relative rounded-2xl shadow-2xl border-2 overflow-hidden"
            style={{ 
              backgroundColor: 'var(--home-card-bg)', 
              borderColor: 'var(--home-card-border)',
              aspectRatio: '16/9',
              width: '100%'
            }}
          >
            {/* Current image with consistent scaling */}
            <Tooltip 
              title={slideshowImages[currentSlide].tooltip} 
              arrow 
              placement="top"
            >
              <img
                src={slideshowImages[currentSlide].src}
                alt={slideshowImages[currentSlide].alt}
                className="w-full h-full object-contain cursor-pointer"
                style={{ 
                  transition: 'opacity 0.5s ease',
                  backgroundColor: 'var(--home-card-bg)'
                }}
              />
            </Tooltip>

            {/* Left arrow */}
            <button
              onClick={prevSlide}
              className="absolute p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
              style={{ 
                backgroundColor: 'var(--modal-background)', 
                color: 'var(--home-text-primary)',
                border: '2px solid var(--home-card-border)',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 20
              }}
            >
              <ChevronLeft size={24} />
            </button>

            {/* Right arrow */}
            <button
              onClick={nextSlide}
              className="absolute p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
              style={{ 
                backgroundColor: 'var(--modal-background)', 
                color: 'var(--home-text-primary)',
                border: '2px solid var(--home-card-border)',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 20
              }}
            >
              <ChevronRight size={24} />
            </button>

            {/* Dots indicator */}
            <div 
              className="absolute flex space-x-2"
              style={{
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 20
              }}
            >
              {slideshowImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentSlide ? 'scale-125' : 'hover:scale-110'
                  }`}
                  style={{
                    backgroundColor: index === currentSlide ? 'var(--home-button-primary)' : 'var(--modal-background)',
                    border: '2px solid var(--home-button-primary)'
                  }}
                />
              ))}
            </div>
          </div>

          {/* Caption */}
          <p 
            className="text-center mt-4 text-sm"
            style={{ color: 'var(--home-text-secondary)' }}
          >
            {slideshowImages[currentSlide].alt} ({currentSlide + 1} of {slideshowImages.length})
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Slideshow;